import React, { useState, useEffect } from 'react';
import ExportToolbar from '../../components/ExportToolbar';
import OperationalMetricsCard from './components/OperationalMetricsCard';
import PatientFlowChart from './components/PatientFlowChart';
import ServiceUtilizationHeatmap from './components/ServiceUtilizationHeatmap';
import OperationalAlertPanel from './components/OperationalAlertPanel';
import TreatmentOutcomesTable from './components/TreatmentOutcomesTable';
import PatientSatisfactionCard from './components/PatientSatisfactionCard';
import AppointmentEfficiencyChart from './components/AppointmentEfficiencyChart';
import PeriodSelector from '../health-system-overview/components/PeriodSelector';
const FacilityOperations = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedPeriod, setSelectedPeriod] = useState({ type: 'yearly', year: new Date()?.getFullYear()?.toString() });

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const operationalMetrics = [
    {
      title: "Daily Patient Volume",
      value: "487",
      unit: "patients",
      change: "+12.3%",
      changeType: "positive",
      icon: "Users",
      threshold: "450",
      status: "normal"
    },
    {
      title: "Bed Occupancy Rate",
      value: "92",
      unit: "%",
      change: "+5.2%",
      changeType: "negative",
      icon: "Bed",
      threshold: "85%",
      status: "warning"
    },
    {
      title: "Average Length of Stay",
      value: "4.2",
      unit: "days",
      change: "-0.3",
      changeType: "positive",
      icon: "Clock",
      threshold: "4.5 days",
      status: "normal"
    },
    {
      title: "Staff-to-Patient Ratio",
      value: "1:8",
      unit: "",
      change: "Stable",
      changeType: "neutral",
      icon: "UserCheck",
      threshold: "1:10",
      status: "normal"
    }
  ];

  const patientFlowData = [
    { time: "8AM", outpatient: 45, emergency: 12, inpatient: 8 },
    { time: "10AM", outpatient: 68, emergency: 15, inpatient: 12 },
    { time: "12PM", outpatient: 82, emergency: 18, inpatient: 15 },
    { time: "2PM", outpatient: 71, emergency: 22, inpatient: 18 },
    { time: "4PM", outpatient: 59, emergency: 16, inpatient: 14 },
    { time: "6PM", outpatient: 38, emergency: 20, inpatient: 10 },
    { time: "8PM", outpatient: 22, emergency: 25, inpatient: 8 }
  ];

  const serviceUtilizationData = {
    timeSlots: ["8AM", "10AM", "12PM", "2PM", "4PM", "6PM", "8PM"],
    departments: [
      {
        name: "Emergency Department",
        utilization: [78, 85, 92, 95, 88, 91, 87]
      },
      {
        name: "Outpatient Clinic",
        utilization: [65, 82, 88, 85, 72, 58, 42]
      },
      {
        name: "Laboratory",
        utilization: [72, 78, 85, 82, 75, 68, 55]
      },
      {
        name: "Radiology",
        utilization: [68, 75, 82, 88, 80, 72, 60]
      },
      {
        name: "Surgical Unit",
        utilization: [85, 90, 95, 92, 88, 82, 75]
      }
    ]
  };

  const operationalAlerts = [
    {
      id: 1,
      title: "Critical Equipment Maintenance Due",
      description: "MRI Scanner #2 requires scheduled maintenance within 48 hours to maintain operational certification.",
      category: "equipment",
      priority: "critical",
      timestamp: "15 minutes ago",
      actionRequired: true
    },
    {
      id: 2,
      title: "Low Supply Stock Alert",
      description: "Surgical gloves inventory below minimum threshold. Current stock: 2 days supply remaining.",
      category: "supply",
      priority: "high",
      timestamp: "1 hour ago",
      actionRequired: true
    },
    {
      id: 3,
      title: "Staff Shortage - Night Shift",
      description: "Emergency department requires 2 additional nurses for tonight's shift due to unexpected absences.",
      category: "staff",
      priority: "critical",
      timestamp: "2 hours ago",
      actionRequired: true
    },
    {
      id: 4,
      title: "Quality Incident Report",
      description: "Medication administration delay reported in Ward B. Investigation initiated, no patient harm occurred.",
      category: "quality",
      priority: "high",
      timestamp: "3 hours ago",
      actionRequired: false
    },
    {
      id: 5,
      title: "Equipment Calibration Scheduled",
      description: "Laboratory analyzers scheduled for quarterly calibration on January 25, 2026.",
      category: "equipment",
      priority: "normal",
      timestamp: "5 hours ago",
      actionRequired: false
    },
    {
      id: 6,
      title: "Supply Order Delivered",
      description: "Monthly pharmaceutical supply order received and verified. All items accounted for.",
      category: "supply",
      priority: "normal",
      timestamp: "6 hours ago",
      actionRequired: false
    }
  ];

  const treatmentOutcomesData = [
    {
      id: 1,
      condition: "Acute Myocardial Infarction",
      successRate: 94.2,
      benchmark: 91.5,
      cases: 127,
      trend: "up"
    },
    {
      id: 2,
      condition: "Hip Replacement Surgery",
      successRate: 96.8,
      benchmark: 95.2,
      cases: 89,
      trend: "up"
    },
    {
      id: 3,
      condition: "Pneumonia Treatment",
      successRate: 88.5,
      benchmark: 90.1,
      cases: 234,
      trend: "down"
    },
    {
      id: 4,
      condition: "Cesarean Section",
      successRate: 97.3,
      benchmark: 96.8,
      cases: 156,
      trend: "stable"
    },
    {
      id: 5,
      condition: "Appendectomy",
      successRate: 98.1,
      benchmark: 97.5,
      cases: 178,
      trend: "up"
    },
    {
      id: 6,
      condition: "Stroke Management",
      successRate: 85.7,
      benchmark: 87.2,
      cases: 92,
      trend: "down"
    },
    {
      id: 7,
      condition: "Diabetes Management",
      successRate: 91.4,
      benchmark: 89.8,
      cases: 412,
      trend: "up"
    },
    {
      id: 8,
      condition: "Cataract Surgery",
      successRate: 99.2,
      benchmark: 98.5,
      cases: 203,
      trend: "up"
    }
  ];

  const patientSatisfactionData = [
    { name: "Excellent", value: 542 },
    { name: "Good", value: 438 },
    { name: "Fair", value: 187 },
    { name: "Poor", value: 80 }
  ];

  const appointmentEfficiencyData = [
    { day: "Mon", scheduled: 320, completed: 295, noShow: 25 },
    { day: "Tue", scheduled: 345, completed: 318, noShow: 27 },
    { day: "Wed", scheduled: 362, completed: 335, noShow: 27 },
    { day: "Thu", scheduled: 338, completed: 310, noShow: 28 },
    { day: "Fri", scheduled: 355, completed: 322, noShow: 33 },
    { day: "Sat", scheduled: 285, completed: 258, noShow: 27 },
    { day: "Sun", scheduled: 190, completed: 172, noShow: 18 }
  ];

  return (
    <div className="min-h-screen bg-background">
      <ExportToolbar />
      <main className="main-content">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
            Facility Operations Dashboard 1
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            Real-time operational monitoring and patient care quality indicators
          </p>


          
          <p className="text-xs md:text-sm text-muted-foreground mt-1">
            Last updated: {currentTime?.toLocaleString('en-US', { 
              month: 'long', 
              day: 'numeric', 
              year: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </p>
        </div>

        <PeriodSelector onPeriodChange={setSelectedPeriod} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          {operationalMetrics?.map((metric, index) => (
            <OperationalMetricsCard key={index} {...metric} />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="lg:col-span-8 space-y-4 md:space-y-6">
            <PatientFlowChart data={patientFlowData} />
            <ServiceUtilizationHeatmap data={serviceUtilizationData} />
            <AppointmentEfficiencyChart data={appointmentEfficiencyData} />
          </div>

          <div className="lg:col-span-4">
            <OperationalAlertPanel alerts={operationalAlerts} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
          <div className="lg:col-span-2">
            <TreatmentOutcomesTable data={treatmentOutcomesData} />
          </div>
          <div className="lg:col-span-1">
            <PatientSatisfactionCard 
              data={patientSatisfactionData} 
              overallScore={4.3}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default FacilityOperations;